/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { LoadingAnimation } from '@/renderer/components/common/LoadingAnimation';
import {
  AccommodationGuest,
  AccommodationGuestDetails,
} from '@/renderer/types/accommodation';
import React, { useEffect } from 'react';
import { formatGuestPreviewCurrency } from '@lib/utils';
import { GuestPreviewErrorState } from './GuestPreviewErrorState';
import { GuestPreviewHeader } from './GuestPreviewHeader';
import { GuestPreviewRecentBookings } from './GuestPreviewRecentBookings';
import { GuestPreviewSalesProfile } from './GuestPreviewSalesProfile';
import { GuestPreviewSection } from './GuestPreviewSection';
import { GuestPreviewStatCard } from './GuestPreviewStatCard';

interface GuestPreviewPanelProps {
  isOpen: boolean;
  guest: AccommodationGuest | null;
  details: AccommodationGuestDetails | null;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
  onRetry: () => void;
  onEdit: () => void;
}

export const GuestPreviewPanel: React.FC<GuestPreviewPanelProps> = ({
  isOpen,
  guest,
  details,
  isLoading,
  error,
  onClose,
  onRetry,
  onEdit,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-slate-950/20 transition-opacity duration-200 ${
          isOpen
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
        aria-hidden={!isOpen}
      />
      <aside
        className={`fixed inset-y-0 right-0 z-50 flex w-full max-w-4xl transform flex-col border-l border-gray-200 bg-[#fcfcfb] shadow-2xl transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!isOpen}
      >
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <LoadingAnimation />
          </div>
        ) : error ? (
          <GuestPreviewErrorState error={error} onRetry={onRetry} />
        ) : details ? (
          <>
            <GuestPreviewHeader
              guest={guest}
              details={details}
              onClose={onClose}
              onEdit={onEdit}
            />
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-8 px-6 py-6 sm:px-8">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <GuestPreviewStatCard
                    label="Total Bookings"
                    value={details.total_bookings.toLocaleString()}
                  />
                  <GuestPreviewStatCard
                    label="Avg. Sales Spend"
                    value={formatGuestPreviewCurrency(
                      details.avg_sales_spend,
                      details.currency,
                    )}
                  />
                  <GuestPreviewStatCard
                    label="Preferred Channel"
                    value={details.preferred_channel || 'Direct'}
                  />
                  <GuestPreviewStatCard
                    label="Cancellation Rate"
                    value={`${details.cancellation_rate.toFixed(0)}%`}
                  />
                </div>

                <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
                  <div className="space-y-8">
                    <GuestPreviewSalesProfile details={details} />

                    <GuestPreviewSection title="Management Notes">
                      <div className="min-h-32 rounded-2xl border border-amber-100 bg-amber-50/40 p-4">
                        <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">
                          {details.management_notes ||
                            'No accommodation notes have been saved for this guest yet.'}
                        </p>
                      </div>
                    </GuestPreviewSection>
                  </div>

                  <GuestPreviewRecentBookings details={details} />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex h-full items-center justify-center px-6">
            <p className="text-sm text-gray-500">
              Select a guest to view details.
            </p>
          </div>
        )}
      </aside>
    </>
  );
};
